import giftCategoryQueries from '@apis/queries/giftCategoryQueries';
import giftItemQueries from '@apis/queries/giftItemQueries';
import BlankEtc from '@components/blank/BlankEtc';
import Chip from '@components/Chip';
import Column from '@components/Column';
import Skeleton from '@components/Skeleton';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { GiftItemStatus, GiftItemType } from '@models/gift';
import { Link } from '@stackflow/link';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function GiftMainFragment() {
    const { t } = useTranslation('gift');
    const priceUnit = usePriceUnit();
    const [giftCategoryId, setGiftCategoryId] = useState<string>();
    const { data: categories } = useQuery(giftCategoryQueries.list());
    const {
        data: items, isPending, isError, refetch,
    } = useQuery(giftItemQueries.catalog(giftCategoryId
        ? { giftCategoryId, status: GiftItemStatus.ON_SALE }
        : { giftCategoryType: ['EXCHANGE', 'BALANCE', 'PREPAID_CARD'], status: GiftItemStatus.ON_SALE }));

    return (
        <section aria-label={t('tabs.main')}>
            <Filters aria-label={t('home.filterLabel')}>
                <Chip isSelected={!giftCategoryId}
                    aria-pressed={!giftCategoryId}
                    onClick={() => setGiftCategoryId(undefined)}>
                    {t('home.filters.ALL')}
                </Chip>
                {categories?.map(({ id, name }) => (
                    <Chip key={id} isSelected={giftCategoryId === id}
                        aria-pressed={giftCategoryId === id}
                        onClick={() => setGiftCategoryId(id)}>
                        {name}
                    </Chip>
                ))}
            </Filters>
            {isPending && (
                <Grid aria-busy='true' aria-label={t('home.loading')}>
                    {Array.from({ length: 6 }, (_, index) => (
                        <Column key={index} fullWidth align='center' spacing={1}>
                            <Skeleton shape='block' height='auto' style={{ aspectRatio: '1', marginBottom: 4 }} />
                            <Skeleton width='80%' height={22} />
                            <Skeleton width='50%' height={22} />
                        </Column>
                    ))}
                </Grid>
            )}
            {isError && (
                <Message>
                    <Text color={colors.textSecondary} align='center'>{t('category.itemsLoadError')}</Text>
                    <Retry aria-label={t('home.retry')} onClick={() => refetch()}>{t('home.retry')}</Retry>
                </Message>
            )}
            {!isPending && !isError && items?.length === 0 && (
                <BlankEtc>
                    <Text color={colors.textSecondary} align='center'>{t('home.empty')}</Text>
                </BlankEtc>
            )}
            {!isPending && !isError && !!items?.length && (
                <Grid>
                    {items?.map((item) => {
                        const content = (
                            <>
                                <Thumbnail src={item.imageUrl} alt={item.name} loading='lazy' />
                                <Text size='body-long' fontWeight='regular'>{item.name}</Text>
                                <Text size='title' fontWeight='medium'>{item.salePrice.toLocaleString()}{priceUnit}</Text>
                            </>
                        );
                        return item.giftItemType === GiftItemType.PRODUCT ? (
                            <Product key={item.id} activityName='GiftItemActivity'
                                activityParams={{ giftItemId: item.id }}>
                                {content}
                            </Product>
                        ) : (
                            <Product key={item.id} activityName='PrepaidCardImagesActivity'
                                activityParams={{ type: 'GIFT', giftItemId: item.id }}>
                                {content}
                            </Product>
                        );
                    })}
                </Grid>
            )}
        </section>
    );
}

const Filters = styled(Chip.Container)`
    overflow-x: auto;
    padding: var(--spacing-4);
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacing-6) var(--spacing-4);
    padding: var(--spacing-4) var(--spacing-4) var(--spacing-10);
`;

const Product = styled(Link)`
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    color: ${colors.gray9};
    text-decoration: none;
    text-align: center;

    &:focus-visible {
        outline: 2px solid ${colors.primary};
        outline-offset: 4px;
    }
`;

const Thumbnail = styled.img`
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: contain;
    border-radius: 4px;
    margin-bottom: var(--spacing-2);
`;

const Message = styled.div`
    padding: var(--spacing-12) var(--spacing-4);
    text-align: center;
`;

const Retry = styled(Chip)`
    margin-top: var(--spacing-4);
`;

export default GiftMainFragment;
